import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

// ── PALETTE ──────────────────────────────────────────────────
const C = {
  bg: '#030b16', bgMid: '#060f1e', bgCard: '#091729', bgPanel: '#071323',
  cyan: '#00b8cc', cyanDim: 'rgba(0,184,204,0.12)', cyanBorder: 'rgba(0,184,204,0.22)',
  orange: '#e85d26', orangeDim: 'rgba(232,93,38,0.12)',
  white: '#f0f4f8', gray: '#7a8799', grayLt: '#b0bcc8',
  border: 'rgba(255,255,255,0.07)', borderMid: 'rgba(255,255,255,0.04)',
  green: '#22c55e', purple: '#8b5cf6', amber: '#f59e0b', red: '#ef4444',
  lavender: '#a78bfa',
};

const API_URL = process.env.REACT_APP_API_URL || '';

// ── ANIMATIONS CSS ───────────────────────────────────────────
const ANIMATIONS_CSS = `
  @keyframes up-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes up-fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes up-scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes up-floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
  @keyframes up-ringPulse {
    0%   { box-shadow: 0 0 0 0 rgba(0,184,204,0.55), 0 0 30px rgba(0,184,204,0.25); }
    70%  { box-shadow: 0 0 0 14px rgba(0,184,204,0.00), 0 0 40px rgba(0,184,204,0.35); }
    100% { box-shadow: 0 0 0 0 rgba(0,184,204,0.00), 0 0 30px rgba(0,184,204,0.25); }
  }
  @keyframes up-shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }
`;

// ── HELPERS ──────────────────────────────────────────────────
const fmtBytes = (n: number): string => {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1024 / 1024).toFixed(2) + ' MB';
};

const fmtDataBR = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '—';
  }
};

// ── TYPES ────────────────────────────────────────────────────
interface Estatisticas {
  ultimo_upload_data: string | null;
  ultimo_upload_filename: string | null;
  dias_desde_ultimo: number | null;
  total_ciclo: number;
  ciclo_ativo_nome: string | null;
}

interface HistRow {
  id: number;
  filename: string;
  file_size_bytes: number;
  data_envio: string;
  ciclo_id: number | null;
  total_inserido: number;
  total_puladas: number;
  total_warnings: number;
  total_erros: number;
  status: string;
}

interface PreviewData {
  total_lido: number;
  total_puladas: number;
  total_a_importar: number;
  violacoes_count: number;
  linhas_puladas?: any[];
  linhas_importar?: any[];
  violacoes?: any[];
  ciclo_id?: number;
}

interface ConfirmResult {
  total_inserido: number;
  total_pendencias_criadas?: number;
  warnings?: any[];
  erros?: any[];
  duplicatas_count?: number;
  erros_reais_count?: number;
  duplicatas_amostra?: any[];
  total_duplicatas?: number;
}

interface Props {
  onVoltar: () => void;
}

// ── BACKGROUND ───────────────────────────────────────────────
const Background: React.FC = () => (
  <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
    <div style={{
      position: 'absolute', inset: 0,
      background: `
        radial-gradient(ellipse 80% 50% at 10% 0%, rgba(0,184,204,0.06) 0%, transparent 60%),
        radial-gradient(ellipse 60% 40% at 90% 100%, rgba(232,93,38,0.05) 0%, transparent 60%),
        radial-gradient(ellipse 40% 60% at 50% 50%, rgba(13,28,53,0.8) 0%, transparent 100%),
        #030b16`
    }} />
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.025 }}>
      <defs>
        <pattern id="up-dotgrid" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.2" fill={C.cyan} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#up-dotgrid)" />
    </svg>
    <div style={{
      position: 'absolute', left: 0, right: 0, height: 120,
      background: `linear-gradient(180deg, transparent, rgba(0,184,204,0.025) 50%, transparent)`,
      animation: 'up-scanline 12s linear infinite'
    }} />
  </div>
);

// ── PAGE HEADER ──────────────────────────────────────────────
const PageHeader: React.FC<{ onVoltar: () => void; estatisticas: Estatisticas | null }> = ({ onVoltar, estatisticas }) => {
  const ultData = fmtDataBR(estatisticas?.ultimo_upload_data || null);
  const dias = estatisticas?.dias_desde_ultimo;
  const totalCiclo = estatisticas?.total_ciclo ?? 0;

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      padding: '28px 40px 24px',
      background: `linear-gradient(120deg, #060f1e 0%, #0a1828 55%, #06101e 100%)`,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <svg style={{ position: 'absolute', right: 0, top: 0, opacity: 0.05, pointerEvents: 'none' }}
        width="500" height="160" viewBox="0 0 500 160">
        {Array.from({ length: 9 }).map((_, i) => <line key={`v${i}`} x1={i * 60} y1="0" x2={i * 60} y2="160" stroke={C.cyan} strokeWidth="1" />)}
        {Array.from({ length: 5 }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 40} x2="500" y2={i * 40} stroke={C.cyan} strokeWidth="1" />)}
      </svg>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
        <div>
          <button onClick={onVoltar} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', marginBottom: 14, borderRadius: 6,
            border: `1px solid ${C.border}`,
            background: 'rgba(255,255,255,0.03)',
            color: C.grayLt, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            fontFamily: "'Inter', sans-serif",
          }}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Voltar
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: `linear-gradient(135deg, ${C.cyan}30, ${C.cyan}08)`,
              border: `1px solid ${C.cyanBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M11 14V4M7 8l4-4 4 4" stroke={C.cyan} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 14v3a2 2 0 002 2h12a2 2 0 002-2v-3" stroke={C.cyan} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: C.white, letterSpacing: '-0.02em', lineHeight: 1, fontFamily: "'Inter', sans-serif", margin: 0 }}>Upload de Planilha</h1>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.cyan, animation: 'up-pulse 2.5s infinite', boxShadow: `0 0 6px ${C.cyan}` }} />
              </div>
              <div style={{ fontSize: 12, color: C.gray, fontFamily: "'JetBrains Mono', monospace", marginTop: 6 }}>
                Importação de dados · FAD-01 · Boletins diários · Lotes semanais
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {[
            { label: 'ÚLT. UPLOAD', value: ultData, sub: dias !== null && dias !== undefined ? (dias === 0 ? 'hoje' : `há ${dias} dia${dias !== 1 ? 's' : ''}`) : '—', color: C.amber },
            { label: 'TOTAL CICLO', value: String(totalCiclo), sub: 'arquivos enviados', color: C.white },
            { label: 'STATUS', value: 'PRONTO', sub: 'aguardando arquivo', color: C.cyan },
          ].map(k => (
            <div key={k.label} style={{
              padding: '12px 18px', borderRadius: 10,
              background: 'rgba(0,184,204,0.04)',
              border: `1px solid ${C.cyanBorder}`,
              minWidth: 108, textAlign: 'left',
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>{k.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, marginTop: 6 }}>{k.value}</div>
              <div style={{ fontSize: 9.5, color: C.gray, marginTop: 4 }}>{k.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── STEP RAIL ────────────────────────────────────────────────
const StepRail: React.FC<{ step: number }> = ({ step }) => {
  const steps = [
    { n: 1, label: 'Selecionar', desc: 'Arquivo .xlsx ou .xls' },
    { n: 2, label: 'Validar', desc: 'Análise da estrutura' },
    { n: 3, label: 'Importar', desc: 'Processar registros' },
    { n: 4, label: 'Concluído', desc: 'Dados disponíveis' },
  ];
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 0,
      padding: '12px 16px',
      background: C.bgPanel,
      border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 18,
    }}>
      {steps.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        const col = done ? C.green : active ? C.cyan : C.gray;
        return (
          <div key={s.n} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: active ? C.cyan : done ? C.green : 'rgba(255,255,255,0.04)',
              border: `1.5px solid ${col}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
              color: active || done ? '#021018' : C.gray,
              boxShadow: active ? `0 0 12px ${C.cyan}88` : 'none',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}>{done ? '✓' : s.n}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: active ? C.white : col, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>{s.label}</div>
              <div style={{ fontSize: 10, color: C.gray, marginTop: 2 }}>{s.desc}</div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 0.7, height: 1, background: `linear-gradient(90deg, ${col}55, ${C.border})`, alignSelf: 'center' }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── DROPZONE ─────────────────────────────────────────────────
const Dropzone: React.FC<{
  setFile: (f: File) => void;
  onPickClick: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}> = ({ setFile, onPickClick, inputRef }) => {
  const [over, setOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      style={{
        position: 'relative', overflow: 'hidden',
        background: over
          ? `linear-gradient(180deg, rgba(0,184,204,0.10), rgba(0,184,204,0.03))`
          : `linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
        border: over ? `1.5px dashed ${C.cyan}` : `1.5px dashed ${C.cyanBorder}`,
        borderRadius: 14,
        padding: '56px 32px',
        textAlign: 'center',
        transition: 'all 0.2s',
        boxShadow: over ? `inset 0 0 60px rgba(0,184,204,0.12)` : 'inset 0 1px 0 rgba(255,255,255,0.03)',
        cursor: 'pointer',
      }}
      onClick={onPickClick}
    >
      {over && (
        <div style={{
          position: 'absolute', top: 0, bottom: 0, width: '40%',
          background: `linear-gradient(90deg, transparent, ${C.cyan}22, transparent)`,
          animation: 'up-shimmer 1.8s ease-in-out infinite',
        }} />
      )}

      {[
        { t: 14, l: 14, a: 'top-left' },
        { t: 14, r: 14, a: 'top-right' },
        { b: 14, l: 14, a: 'bot-left' },
        { b: 14, r: 14, a: 'bot-right' },
      ].map((p: any, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: p.t, left: p.l, right: p.r, bottom: p.b,
          width: 18, height: 18,
          borderTop: p.a.startsWith('top') ? `2px solid ${over ? C.cyan : C.cyanBorder}` : 'none',
          borderBottom: p.a.startsWith('bot') ? `2px solid ${over ? C.cyan : C.cyanBorder}` : 'none',
          borderLeft: p.a.endsWith('left') ? `2px solid ${over ? C.cyan : C.cyanBorder}` : 'none',
          borderRight: p.a.endsWith('right') ? `2px solid ${over ? C.cyan : C.cyanBorder}` : 'none',
        }} />
      ))}

      <div style={{
        width: 84, height: 84, margin: '0 auto 18px', borderRadius: '50%',
        background: `radial-gradient(circle at 30% 30%, ${C.cyan}33, ${C.cyan}08)`,
        border: `1.5px solid ${C.cyanBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: over ? 'up-ringPulse 1.8s ease-in-out infinite' : 'up-floatY 3.5s ease-in-out infinite',
      }}>
        <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
          <path d="M19 24V8M11 16l8-8 8 8" stroke={over ? C.white : C.cyan} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6 26v3a3 3 0 003 3h20a3 3 0 003-3v-3" stroke={over ? C.white : C.cyan} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div style={{ fontSize: 18, fontWeight: 700, color: C.white, marginBottom: 6, fontFamily: "'Inter', sans-serif" }}>
        {over ? 'Solte o arquivo aqui' : 'Arraste sua planilha ou clique para selecionar'}
      </div>
      <div style={{ fontSize: 12, color: C.gray, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.04em' }}>
        Aceito: .xlsx · .xls · .csv &nbsp;·&nbsp; Máx 25 MB
      </div>

      <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center', gap: 10 }}>
        <button onClick={(e) => { e.stopPropagation(); onPickClick(); }} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 18px', borderRadius: 8,
          border: `1px solid ${C.cyan}`,
          background: C.cyan, color: '#021018',
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
          fontFamily: 'Inter,sans-serif',
          boxShadow: `0 4px 14px ${C.cyan}55`,
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 8.5V11a1 1 0 001 1h6a1 1 0 001-1V8.5M7 8.5V2M4.5 4.5L7 2l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Selecionar arquivo
        </button>
      </div>

      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
    </div>
  );
};

// ── FILE CARD ────────────────────────────────────────────────
const FileCard: React.FC<{
  file: File;
  onClear: () => void;
  onSubmit: () => void;
  progress: number;
  status: string;
}> = ({ file, onClear, onSubmit, progress, status }) => {
  const ext = (file.name.split('.').pop() || '').toUpperCase();
  const extColor = ext === 'CSV' ? C.amber : ext === 'XLS' ? C.lavender : C.green;
  return (
    <div style={{
      background: `linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)',
      animation: 'up-fadeIn 0.4s ease',
    }}>
      <div style={{ height: 3, background: `linear-gradient(90deg, ${C.cyan}, ${C.orange}aa)` }} />
      <div style={{ padding: '22px 24px', display: 'flex', alignItems: 'center', gap: 18 }}>
        <div style={{
          width: 68, height: 68, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${extColor}28, ${extColor}08)`,
          border: `1.5px solid ${extColor}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}>
          <svg width="32" height="38" viewBox="0 0 32 38" fill="none">
            <path d="M4 2h16l8 8v26a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2z" stroke={extColor} strokeWidth="1.6" fill={`${extColor}10`} />
            <path d="M20 2v8h8" stroke={extColor} strokeWidth="1.6" />
            <path d="M10 20l4 6 4-6M10 32l4-6 4 6" stroke={extColor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{
            position: 'absolute', bottom: -6, right: -6,
            padding: '2px 6px', borderRadius: 4,
            background: extColor, color: '#021018',
            fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
            fontFamily: "'JetBrains Mono', monospace",
          }}>{ext}</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: C.white, marginBottom: 6,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            fontFamily: "'Inter', sans-serif",
          }}>
            {file.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>
            <span>{fmtBytes(file.size)}</span>
            <span>{new Date(file.lastModified || Date.now()).toLocaleDateString('pt-BR')}</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '1px 8px', borderRadius: 4,
              background: `${C.cyan}15`, color: C.cyan,
              border: `1px solid ${C.cyan}44`,
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.cyan, animation: 'up-pulse 2s infinite' }} />
              {status}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={onClear} disabled={progress > 0 && progress < 100} style={{
            padding: '9px 14px', borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: 'rgba(255,255,255,0.03)',
            color: C.grayLt, fontSize: 12, fontWeight: 600,
            cursor: progress > 0 && progress < 100 ? 'not-allowed' : 'pointer',
            opacity: progress > 0 && progress < 100 ? 0.4 : 1,
            fontFamily: "'Inter', sans-serif",
          }}>Trocar</button>
          <button onClick={onSubmit} disabled={progress > 0} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 18px', borderRadius: 8,
            border: `1px solid ${C.cyan}`,
            background: progress > 0 ? 'transparent' : C.cyan,
            color: progress > 0 ? C.cyan : '#021018',
            fontSize: 12, fontWeight: 700, cursor: progress > 0 ? 'not-allowed' : 'pointer',
            boxShadow: progress > 0 ? 'none' : `0 4px 14px ${C.cyan}44`,
            fontFamily: "'Inter', sans-serif",
          }}>
            {progress >= 100 ? '✓ Importado' : progress > 0 ? `${Math.round(progress)}%` : 'Importar'}
          </button>
        </div>
      </div>

      {progress > 0 && (
        <div style={{ padding: '0 24px 18px' }}>
          <div style={{
            height: 6, borderRadius: 6,
            background: 'rgba(255,255,255,0.05)',
            overflow: 'hidden', position: 'relative',
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${C.cyan}, ${C.orange})`,
              borderRadius: 6,
              transition: 'width 0.2s ease',
              boxShadow: `0 0 12px ${C.cyan}`,
            }} />
            {progress < 100 && (
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
                animation: 'up-shimmer 1.4s ease-in-out infinite',
                width: '30%',
              }} />
            )}
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginTop: 8,
            fontSize: 10.5, fontFamily: "'JetBrains Mono', monospace", color: C.gray,
            letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            <span>{progress < 30 ? 'Lendo arquivo' : progress < 70 ? 'Validando estrutura' : progress < 100 ? 'Salvando registros' : '✓ Concluído'}</span>
            <span style={{ color: progress >= 100 ? C.green : C.cyan, fontWeight: 700 }}>{Math.round(progress)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── SIDEBAR ──────────────────────────────────────────────────
const SidebarInfo: React.FC<{ historico: HistRow[]; loadingHist: boolean }> = ({ historico, loadingHist }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div style={{
      background: C.bgPanel,
      border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${C.cyan}`,
      borderRadius: 12, padding: '18px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke={C.cyan} strokeWidth="1.3" /><path d="M7 4v3.5M7 10v.3" stroke={C.cyan} strokeWidth="1.4" strokeLinecap="round" /></svg>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.cyan, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>Como funciona</span>
      </div>
      {[
        { n: 1, t: 'Exporte a planilha do FAD-01 do sistema oficial.' },
        { n: 2, t: 'Use o modelo padrão — colunas e cabeçalhos não podem ser alterados.' },
        { n: 3, t: 'Após o upload, os dados serão validados e processados automaticamente.' },
        { n: 4, t: 'Você poderá conferir os registros no Resumo Semanal.' },
      ].map(s => (
        <div key={s.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <span style={{
            flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
            background: `${C.cyan}1a`, border: `1px solid ${C.cyan}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 800, color: C.cyan,
            fontFamily: "'JetBrains Mono', monospace",
          }}>{s.n}</span>
          <span style={{ fontSize: 12, color: C.grayLt, lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }}>{s.t}</span>
        </div>
      ))}
    </div>

    <div style={{
      background: C.bgPanel,
      border: `1px solid ${C.border}`, borderRadius: 12,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${C.border}`,
        background: 'rgba(0,184,204,0.03)',
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.cyan, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>Histórico recente</span>
        <span style={{ fontSize: 10, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>{historico.length} uploads</span>
      </div>
      <div>
        {loadingHist ? (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>
            Carregando...
          </div>
        ) : historico.length === 0 ? (
          <div style={{ padding: '28px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: C.grayLt, fontFamily: "'Inter', sans-serif", marginBottom: 6 }}>
              Sem uploads recentes
            </div>
            <div style={{ fontSize: 10.5, color: C.gray, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5 }}>
              Os registros aparecerão aqui<br />após o primeiro upload.
            </div>
          </div>
        ) : historico.map((r, i) => (
          <div key={r.id} style={{
            padding: '11px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            borderBottom: i < historico.length - 1 ? `1px solid ${C.borderMid}` : 'none',
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 11.5, color: C.white, fontFamily: "'JetBrains Mono', monospace",
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{r.filename}</div>
              <div style={{ fontSize: 10, color: C.gray, marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
                {fmtDataBR(r.data_envio)} · {fmtBytes(r.file_size_bytes)} · {r.total_inserido} reg.
              </div>
            </div>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
              padding: '3px 8px', borderRadius: 4,
              background: r.status === 'ok' ? `${C.green}1a` : r.status === 'erro' ? `${C.red}1a` : `${C.amber}1a`,
              color: r.status === 'ok' ? C.green : r.status === 'erro' ? C.red : C.amber,
              border: `1px solid ${r.status === 'ok' ? C.green : r.status === 'erro' ? C.red : C.amber}44`,
              fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase',
            }}>{r.status === 'ok' ? 'OK' : r.status === 'erro' ? 'ERRO' : 'AVISOS'}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── ROOT ─────────────────────────────────────────────────────
const UploadPlanilha: React.FC<Props> = ({ onVoltar }) => {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [historico, setHistorico] = useState<HistRow[]>([]);
  const [loadingHist, setLoadingHist] = useState(true);

  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [resultado, setResultado] = useState<ConfirmResult | null>(null);
  const [errMsg, setErrMsg] = useState('');

  const fetchEstatisticas = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/upload/estatisticas`);
      setEstatisticas(r.data);
    } catch {
      setEstatisticas(null);
    }
  }, []);

  const fetchHistorico = useCallback(async () => {
    setLoadingHist(true);
    try {
      const r = await axios.get(`${API_URL}/api/upload/historico`);
      setHistorico(Array.isArray(r.data) ? r.data : []);
    } catch {
      setHistorico([]);
    } finally {
      setLoadingHist(false);
    }
  }, []);

  useEffect(() => {
    fetchEstatisticas();
    fetchHistorico();
  }, [fetchEstatisticas, fetchHistorico]);

  useEffect(() => {
    if (file && !resultado) setStep(2);
    else if (!file) setStep(1);
  }, [file, resultado]);

  const onSubmit = useCallback(async () => {
    if (!file) return;
    setErrMsg('');
    setStep(3);
    setProgress(5);

    try {
      const fd = new FormData();
      fd.append('file', file);
      const previewResp = await axios.post(`${API_URL}/api/upload/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.min(40, 5 + Math.round((e.loaded / e.total) * 35)));
        },
      });
      setPreview(previewResp.data);
      setProgress(50);

      const confirmResp = await axios.post(`${API_URL}/api/upload/confirmar`, {});
      setProgress(100);
      setResultado(confirmResp.data);
      setStep(4);

      fetchEstatisticas();
      fetchHistorico();
    } catch (e: any) {
      setProgress(0);
      setStep(2);
      const msg = e?.response?.data?.detail || e?.message || 'Erro ao processar';
      setErrMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  }, [file, fetchEstatisticas, fetchHistorico]);

  const onClear = () => {
    setFile(null);
    setProgress(0);
    setPreview(null);
    setResultado(null);
    setErrMsg('');
    setStep(1);
  };

  const onPickClick = () => inputRef.current?.click();

  const totalInserido = resultado?.total_inserido ?? 0;
  const warnings = resultado?.warnings || [];
  const erros = resultado?.erros || [];
  const totalPendencias = resultado?.total_pendencias_criadas ?? 0;
  const duplicatasCount = resultado?.duplicatas_count ?? resultado?.total_duplicatas ?? 0;

  return (
    <div style={{ minHeight: '100vh', position: 'relative', background: C.bg }}>
      <style>{ANIMATIONS_CSS}</style>
      <Background />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <PageHeader onVoltar={onVoltar} estatisticas={estatisticas} />
        <main style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 40px 48px' }}>
          <StepRail step={step} />

          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 22, alignItems: 'flex-start' }}>
            <div>
              {!file ? (
                <Dropzone setFile={setFile} onPickClick={onPickClick} inputRef={inputRef} />
              ) : (
                <FileCard file={file} onClear={onClear} onSubmit={onSubmit}
                  progress={progress}
                  status={progress >= 100 ? 'Importado' : progress > 0 ? 'Processando' : 'Pronto para importar'} />
              )}

              {file && (
                <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setProgress(0); setStep(2); } }} />
              )}

              {errMsg && (
                <div style={{
                  marginTop: 18, padding: '14px 18px',
                  background: `${C.red}15`, border: `1px solid ${C.red}55`,
                  borderRadius: 10,
                  fontSize: 12.5, color: C.red,
                  fontFamily: "'Inter', sans-serif",
                  animation: 'up-fadeIn 0.3s ease',
                }}>
                  <strong>Erro:</strong> {errMsg}
                </div>
              )}

              {resultado && progress >= 100 && (
                <div style={{
                  marginTop: 18, padding: '18px 20px',
                  background: `linear-gradient(135deg, ${C.green}1a, ${C.green}04)`,
                  border: `1px solid ${C.green}55`,
                  borderRadius: 12,
                  display: 'flex', alignItems: 'center', gap: 14,
                  animation: 'up-fadeIn 0.4s ease',
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: C.green, color: '#021018',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, fontWeight: 900, boxShadow: `0 0 16px ${C.green}88`,
                  }}>✓</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.white, fontFamily: "'Inter', sans-serif" }}>Planilha importada com sucesso</div>
                    <div style={{ fontSize: 11.5, color: C.grayLt, marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>
                      {totalInserido} registros inseridos · {totalPendencias} pendências criadas
                    </div>
                  </div>
                </div>
              )}

              {warnings.length > 0 && (
                <div style={{
                  marginTop: 14,
                  background: C.bgPanel,
                  border: `1px solid ${C.amber}55`,
                  borderLeft: `3px solid ${C.amber}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  animation: 'up-fadeIn 0.4s ease',
                }}>
                  <div style={{ padding: '12px 18px', background: `${C.amber}10`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.amber, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                      ⚠ Avisos ({warnings.length})
                    </span>
                  </div>
                  <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {warnings.map((w, i) => (
                      <div key={i} style={{
                        padding: '10px 18px',
                        borderTop: i > 0 ? `1px solid ${C.borderMid}` : 'none',
                        fontSize: 11.5, color: C.grayLt, fontFamily: "'JetBrains Mono', monospace",
                      }}>
                        {typeof w === 'string' ? w : (w.motivo || w.message || JSON.stringify(w))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Card de DUPLICATAS — neutro, não é erro */}
              {duplicatasCount > 0 && (
                <div style={{
                  marginTop: 14,
                  background: C.bgPanel,
                  border: `1px solid ${C.cyanBorder}`,
                  borderLeft: `3px solid ${C.cyan}`,
                  borderRadius: 12,
                  padding: '14px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  animation: 'up-fadeIn 0.4s ease',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: `${C.cyan}1a`, border: `1px solid ${C.cyan}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <circle cx="9" cy="9" r="7" stroke={C.cyan} strokeWidth="1.5"/>
                      <path d="M9 5.5v4M9 12v.5" stroke={C.cyan} strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "'Inter', sans-serif" }}>
                      Linhas já cadastradas — ignoradas pelo sistema
                    </div>
                    <div style={{ fontSize: 11.5, color: C.grayLt, marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>
                      {duplicatasCount} {duplicatasCount === 1 ? 'registro já existia' : 'registros já existiam'} no banco · sem ação necessária
                    </div>
                  </div>
                </div>
              )}

              {/* Card de ERROS REAIS — só aparece se houver erros que NÃO são duplicatas */}
              {erros.length > 0 && (
                <div style={{
                  marginTop: 14,
                  background: C.bgPanel,
                  border: `1px solid ${C.red}55`,
                  borderLeft: `3px solid ${C.red}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  animation: 'up-fadeIn 0.4s ease',
                }}>
                  <div style={{ padding: '12px 18px', background: `${C.red}10`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.red, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                      ✗ Linhas com erro ({erros.length})
                    </span>
                  </div>
                  <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {erros.map((er, i) => (
                      <div key={i} style={{
                        padding: '10px 18px',
                        borderTop: i > 0 ? `1px solid ${C.borderMid}` : 'none',
                        fontSize: 11.5, color: C.grayLt, fontFamily: "'JetBrains Mono', monospace",
                      }}>
                        {typeof er === 'string' ? er : (er.motivo || er.message || JSON.stringify(er))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                marginTop: 16, padding: '12px 16px',
                background: 'rgba(255,255,255,0.02)',
                border: `1px dashed ${C.border}`, borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 10,
                fontSize: 11.5, color: C.grayLt,
                fontFamily: "'Inter', sans-serif",
              }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 2l1.7 3.5 3.8.5-2.8 2.7.7 3.8L7 10.6l-3.4 1.9.7-3.8L1.5 6l3.8-.5L7 2z" stroke={C.amber} strokeWidth="1.2" strokeLinejoin="round" />
                </svg>
                <span><strong style={{ color: C.amber }}>Dica:</strong> envie um arquivo por SE. Re-uploads sobrescrevem dados anteriores da mesma semana.</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
                <div style={{
                  background: C.bgPanel,
                  border: `1px solid ${C.border}`, borderRadius: 12,
                  padding: '16px 18px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3 3 7-7" stroke={C.green} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.green, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                      Validação automática
                    </span>
                  </div>
                  {[
                    'Estrutura do modelo FAD-01',
                    'Datas dentro do ciclo informado',
                    'Agentes cadastrados no sistema',
                    'Áreas e quarteirões existentes',
                    'Somatórios e totais coerentes',
                  ].map(item => (
                    <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 8 }}>
                      <span style={{
                        flexShrink: 0, marginTop: 3,
                        width: 12, height: 12, borderRadius: 3,
                        background: `${C.green}1a`, border: `1px solid ${C.green}66`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke={C.green} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </span>
                      <span style={{ fontSize: 11.5, color: C.grayLt, lineHeight: 1.45, fontFamily: "'Inter', sans-serif" }}>{item}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  background: C.bgPanel,
                  border: `1px solid ${C.border}`, borderRadius: 12,
                  padding: '16px 18px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 3h10M2 7h10M2 11h6" stroke={C.cyan} strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.cyan, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                      Modulos atualizados
                    </span>
                  </div>
                  {[
                    { mod: 'Resumo Semanal', color: C.cyan, desc: 'Tabela diaria da SE' },
                    { mod: 'FAD-07', color: C.orange, desc: 'Consolidado por area' },
                    { mod: 'Boletim de Ponto', color: C.amber, desc: 'Frequencia de agentes' },
                    { mod: 'Pendencias', color: C.lavender, desc: 'Fechados e recusados' },
                    { mod: 'Painel Agente', color: C.green, desc: 'Indicadores individuais' },
                  ].map(m => (
                    <div key={m.mod} style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
                      <span style={{
                        flexShrink: 0, width: 6, height: 6, borderRadius: '50%',
                        background: m.color, boxShadow: `0 0 6px ${m.color}88`,
                      }} />
                      <span style={{ fontSize: 11.5, color: C.white, fontWeight: 600, minWidth: 120, fontFamily: "'Inter', sans-serif" }}>{m.mod}</span>
                      <span style={{ fontSize: 11, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>{m.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{
                marginTop: 14,
                background: C.bgPanel,
                border: `1px solid ${C.border}`, borderRadius: 12,
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '12px 18px',
                  borderBottom: `1px solid ${C.border}`,
                  background: 'rgba(232,93,38,0.04)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1.5L13 12H1L7 1.5z" stroke={C.orange} strokeWidth="1.4" strokeLinejoin="round" />
                    <path d="M7 6v2.5M7 10v.3" stroke={C.orange} strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: C.orange, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                    Problemas comuns
                  </span>
                </div>
                <div>
                  {[
                    { q: 'Erro de leitura da planilha', a: 'Verifique se o arquivo nao esta corrompido ou aberto em outro programa.' },
                    { q: 'Datas fora do ciclo', a: 'Confirme se o ciclo selecionado corresponde ao periodo da planilha.' },
                    { q: 'Agente nao encontrado', a: 'Cadastre o agente em Agentes antes de importar a planilha.' },
                  ].map((it, i, a) => (
                    <div key={i} style={{
                      padding: '12px 18px',
                      borderBottom: i < a.length - 1 ? `1px solid ${C.borderMid}` : 'none',
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                    }}>
                      <span style={{
                        flexShrink: 0,
                        fontSize: 9.5, fontWeight: 800, color: C.orange,
                        padding: '2px 7px', borderRadius: 4,
                        background: `${C.orange}1a`, border: `1px solid ${C.orange}44`,
                        fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em',
                        marginTop: 2,
                      }}>!</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.white, fontFamily: "'Inter', sans-serif" }}>{it.q}</div>
                        <div style={{ fontSize: 11, color: C.gray, marginTop: 3, lineHeight: 1.45, fontFamily: "'Inter', sans-serif" }}>{it.a}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <SidebarInfo historico={historico} loadingHist={loadingHist} />
          </div>
        </main>
        <div style={{ textAlign: 'center', padding: '24px 40px', borderTop: `1px solid ${C.border}`, marginTop: 24 }}>
          <span style={{ fontSize: 11, color: C.gray, fontFamily: "'Inter', sans-serif" }}>
            (c) 2026 Tovis - Desenvolvido por <span style={{ color: C.cyan, fontWeight: 500 }}>Walber Reis</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default UploadPlanilha;
